/**
 * QueueAnimation - Animated queue illustration composed of layered character sprites
 * Uses animated webp files for each character positioned over a background
 */

// Background
import background from "../assets/animated/queue-background.png";
// Desktop character sprites
import blueDesktop from "../assets/animated/queue-blue.webp";
// Mobile character sprites
import blueMobile from "../assets/animated/queue-blue-mobile.webp";
import greenDesktop from "../assets/animated/queue-green.webp";
import greenMobile from "../assets/animated/queue-green-mobile.webp";
import orangeDesktop from "../assets/animated/queue-orange.webp";
import orangeMobile from "../assets/animated/queue-orange-mobile.webp";
import yellowDesktop from "../assets/animated/queue-yellow.webp";
import yellowMobile from "../assets/animated/queue-yellow-mobile.webp";

interface CharacterConfig {
  name: string;
  desktop: string;
  mobile: string;
  /** Width as percentage of container */
  widthPercent: number;
  /** Left position as percentage of container */
  leftPercent: number;
  /** Bottom offset as percentage of container height (for vertical positioning) */
  bottomPercent: number;
  /** Z-index for layering (higher = in front) */
  zIndex: number;
}

// Character configurations - ordered left to right in the queue
// Positions are percentages relative to the container
const characters: CharacterConfig[] = [
  {
    name: "orange",
    desktop: orangeDesktop,
    mobile: orangeMobile,
    widthPercent: 18,
    leftPercent: 5,
    bottomPercent: 16,
    zIndex: 10,
  },
  {
    name: "yellow",
    desktop: yellowDesktop,
    mobile: yellowMobile,
    widthPercent: 22.14,
    leftPercent: 24,
    bottomPercent: 15,
    zIndex: 20,
  },
  {
    name: "blue",
    desktop: blueDesktop,
    mobile: blueMobile,
    widthPercent: 14.04,
    leftPercent: 46,
    bottomPercent: 15,
    zIndex: 30,
  },
  {
    name: "green",
    desktop: greenDesktop,
    mobile: greenMobile,
    widthPercent: 21.6,
    leftPercent: 62,
    bottomPercent: 15,
    zIndex: 40,
  },
];

export interface QueueAnimationProps {
  /** Alt text for the queue illustration */
  alt: string;
  /** Additional className for the container */
  className?: string;
}

export function QueueAnimation({ alt, className }: QueueAnimationProps) {
  return (
    <div
      className={`relative w-full h-full min-h-0 ${className ?? ""}`}
      style={{ aspectRatio: "2237 / 897", maxHeight: "100%" }}
      role="img"
      aria-label={alt}
    >
      {/* Background layer */}
      <img
        src={background}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-bottom"
      />

      {/* Character layers */}
      {characters.map((char) => (
        <div
          key={char.name}
          className="absolute"
          style={{
            left: `${char.leftPercent}%`,
            bottom: `${char.bottomPercent}%`,
            width: `${char.widthPercent}%`,
            zIndex: char.zIndex,
          }}
        >
          {/* Desktop version */}
          <img src={char.desktop} alt="" aria-hidden="true" className="hidden h-auto w-full md:block" />
          {/* Mobile version */}
          <img src={char.mobile} alt="" aria-hidden="true" className="block h-auto w-full md:hidden" />
        </div>
      ))}
    </div>
  );
}
